import { useState, useEffect } from 'react';
import { IncomeMiscCalculatedOn } from 'views/ProFormaWizard/constants';
import { IOtherIncome } from '../types';

interface UseOtherIncomeManagerProps {
    initialIncomes?: IOtherIncome[];
}

const DEFAULT_INCOMES: IOtherIncome[] = [ // Is this still needed? We create the default line items in the BE
    {
        id: 1,
        name: 'Ratio Utility Billing System (RUBS)',
        annualGrowth: 0.05,
        valuePerUnitPerMonth: 50,
        amountPerYear: 600,
        percentageOfTotalRentalIncome: 0,
        calculatedOn: IncomeMiscCalculatedOn.AMOUNT_PER_YEAR,
    },
    {
        id: 2,
        name: 'Parking',
        annualGrowth: 0.03,
        valuePerUnitPerMonth: 100,
        amountPerYear: 1200,
        percentageOfTotalRentalIncome: 0,
        calculatedOn: IncomeMiscCalculatedOn.AMOUNT_PER_YEAR,
    },
];

export const useOtherIncomeManager = ({ initialIncomes = [] }: UseOtherIncomeManagerProps) => {
    const [incomeArray, setIncomeArray] = useState<IOtherIncome[]>(() => {
        if (initialIncomes?.length > 0) {
            return initialIncomes.map(income => ({
                id: income.id || Date.now(),
                name: income.name || '',
                annualGrowth: Number(income.annualGrowth),
                valuePerUnitPerMonth: Number(income.valuePerUnitPerMonth) || 0,
                amountPerYear: Number(income.amountPerYear) || 0,
                percentageOfTotalRentalIncome: Number(income.percentageOfTotalRentalIncome) * 100,
                calculatedOn: income.calculatedOn || IncomeMiscCalculatedOn.AMOUNT_PER_YEAR,
            }));
        }
        return DEFAULT_INCOMES;
    });

    // Only update when initialIncomes changes and is not empty
    useEffect(() => {
        if (initialIncomes?.length > 0) {
            const newIncomes = initialIncomes.map(income => ({
                id: income.id || Date.now(),
                name: income.name || '',
                annualGrowth: Number(income.annualGrowth),
                valuePerUnitPerMonth: Number(income.valuePerUnitPerMonth) || 0,
                amountPerYear: Number(income.amountPerYear) || 0,
                percentageOfTotalRentalIncome: Number(income.percentageOfTotalRentalIncome) * 100,
                calculatedOn: income.calculatedOn || IncomeMiscCalculatedOn.AMOUNT_PER_YEAR,
            }));
            setIncomeArray(newIncomes);
        }
    }, [initialIncomes]);

    const handleAddOrDuplicateIncome = (incomeId?: number) => {
        setIncomeArray(prevArray => {
            if (!incomeId) {
                return [...prevArray, {
                    id: Date.now(),
                    name: '',
                    annualGrowth: 0.03,
                    valuePerUnitPerMonth: 0,
                    amountPerYear: 0,
                    percentageOfTotalRentalIncome: 0,
                    calculatedOn: IncomeMiscCalculatedOn.AMOUNT_PER_YEAR,
                }];
            }

            const incomeToDuplicate = prevArray.find(income => income.id === incomeId);
            if (!incomeToDuplicate) return prevArray;

            const duplicatedIncome = {
                ...incomeToDuplicate,
                id: Date.now(),
            };
            return [...prevArray, duplicatedIncome];
        });
    };

    const handleDeleteIncome = (incomeId: number) => {
        setIncomeArray(prevArray => prevArray.filter(income => income.id !== incomeId));
    };

    const handleUpdateIncome = (updatedIncome: IOtherIncome) => {
        setIncomeArray(prevArray => prevArray.map(income => {
            if (income.id === updatedIncome.id) {
                const annualGrowth = Math.min(1, Math.max(0, Number(updatedIncome.annualGrowth || 0)));
                const percentageOfTotalRentalIncome = Math.min(100, Math.max(0, Number(updatedIncome.percentageOfTotalRentalIncome || 0)));

                return {
                    ...income,
                    ...updatedIncome,
                    annualGrowth,
                    valuePerUnitPerMonth: Number(updatedIncome.valuePerUnitPerMonth || 0),
                    amountPerYear: Number(updatedIncome.amountPerYear || 0),
                    percentageOfTotalRentalIncome,
                };
            }
            return income;
        }));
    };

    const getApiData = () => incomeArray.map(income => ({
        ...income,
        annualGrowth: Number(income.annualGrowth) || 0,
        percentageOfTotalRentalIncome: (Number(income.percentageOfTotalRentalIncome) || 0) / 100,
    }));

    return {
        incomeArray,
        handleAddOrDuplicateIncome,
        handleDeleteIncome,
        handleUpdateIncome,
        setIncomeArray,
        getApiData,
    };
};
